import { useEffect, useRef, useState, memo } from "react";
import { useJsApiLoader, GoogleMap } from "@react-google-maps/api";
import type { ReportEvent } from "../../../store/reports/reportEventsStore";
import { formatDateTime } from "../utils/reportPointProcessors";
import { resolveEventLabel } from "../utils/eventLabels";
import { escapeHtml } from "../../../utils/escapeHtml";

const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry")[] = ["places"];
const DEFAULT_CENTER = { lat: 23.6345, lng: -102.5528 };
const DEFAULT_ZOOM = 5;

const ROUTE_COLOR = "#334155";
const SELECTED_COLOR = "#2563EB";
const START_COLOR = "#10B981";
const END_COLOR = "#EF4444";
const MAX_GAP_MS = 15 * 60 * 1000;
const HIT_STROKE_WEIGHT = 32;

const buildEventSegments = (events: ReportEvent[]): google.maps.LatLngLiteral[][] => {
  const segments: google.maps.LatLngLiteral[][] = [];
  let current: google.maps.LatLngLiteral[] = [];

  events.forEach((event, i) => {
    const prev = events[i - 1];
    if (prev && event.ts - prev.ts > MAX_GAP_MS) {
      if (current.length > 1) segments.push(current);
      current = [];
    }
    current.push({ lat: event.lat, lng: event.lng });
  });

  if (current.length > 1) segments.push(current);
  return segments;
};

export interface SelectedEventPoint {
  deviceId: string;
  vehicleName: string;
  ts: number;
  lat: number;
  lng: number;
  fecha: string;
  evento: string;
  eventCode: number | null;
}

export interface MapEventGroup {
  deviceId: string;
  vehicleName: string;
  color: string;
  events: ReportEvent[];
}

const popupHtml = (
  fecha: string,
  evento: string,
  vehicleName: string | null,
  variant: "hover" | "pinned",
): string =>
  `<div class="report-map-popup report-map-popup--${variant}">
    <div class="report-map-popup__title">${escapeHtml(fecha)}</div>
    <div>${escapeHtml(evento)}</div>
    ${vehicleName ? `<div>${escapeHtml(vehicleName)}</div>` : ""}
  </div>`;

const eventName = (event: ReportEvent): string =>
  resolveEventLabel(event.eventCode, event.eventType);

const buildSelected = (
  event: ReportEvent,
  deviceId: string,
  vehicleName: string,
  tz: string,
): SelectedEventPoint => ({
  deviceId,
  vehicleName,
  ts: event.ts,
  lat: event.lat,
  lng: event.lng,
  fecha: formatDateTime(event.ts, tz),
  evento: eventName(event),
  eventCode: event.eventCode,
});

interface EventRouteProps {
  map: google.maps.Map;
  events: ReportEvent[];
  color: string;
  colorByUnit: boolean;
  tz: string;
  deviceId: string;
  vehicleName: string;
  showVehicle: boolean;
  hasSelection: boolean;
  onSelect: (event: SelectedEventPoint) => void;
}

const EventRoute = ({
  map,
  events,
  color,
  colorByUnit,
  tz,
  deviceId,
  vehicleName,
  showVehicle,
  hasSelection,
  onSelect,
}: EventRouteProps) => {
  const suppressRef = useRef(hasSelection);

  useEffect(() => {
    suppressRef.current = hasSelection;
  }, [hasSelection]);

  useEffect(() => {
    if (!map || events.length === 0) return;

    const lines: google.maps.Polyline[] = [];
    const markers: google.maps.Marker[] = [];
    const listeners: google.maps.MapsEventListener[] = [];
    const lineColor = colorByUnit ? color : ROUTE_COLOR;

    buildEventSegments(events).forEach((path) => {
      const segment = new google.maps.Polyline({
        path,
        strokeColor: lineColor,
        strokeOpacity: 0.7,
        strokeWeight: 3,
        clickable: false,
        zIndex: 1,
      });
      segment.setMap(map);
      lines.push(segment);
    });

    const info = new google.maps.InfoWindow({ disableAutoPan: true });

    let lastInfoKey = "";
    let infoOpen = false;
    let closeTimer = 0;

    const openInfo = (content: string, position: google.maps.LatLngLiteral) => {
      window.clearTimeout(closeTimer);
      info.setPosition(position);
      if (content !== lastInfoKey) {
        info.setContent(content);
        lastInfoKey = content;
      }
      if (infoOpen) return;
      info.open(map);
      infoOpen = true;
    };

    const closeInfo = () => {
      window.clearTimeout(closeTimer);
      lastInfoKey = "";
      if (!infoOpen) return;
      info.close();
      infoOpen = false;
    };

    const scheduleCloseInfo = () => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(closeInfo, 160);
    };

    const findNearest = (latLng: google.maps.LatLng): ReportEvent => {
      const lat = latLng.lat();
      const lng = latLng.lng();
      let best = events[0];
      let bestDist = Infinity;
      for (const e of events) {
        const dx = e.lng - lng;
        const dy = e.lat - lat;
        const d = dx * dx + dy * dy;
        if (d < bestDist) {
          bestDist = d;
          best = e;
        }
      }
      return best;
    };

    if (events.length > 1) {
      const hitLine = new google.maps.Polyline({
        path: events.map((e) => ({ lat: e.lat, lng: e.lng })),
        strokeColor: "#000000",
        strokeOpacity: 0,
        strokeWeight: HIT_STROKE_WEIGHT,
        clickable: true,
        zIndex: 4,
      });
      hitLine.setMap(map);
      lines.push(hitLine);

      listeners.push(
        hitLine.addListener("mousemove", (e: google.maps.MapMouseEvent) => {
          if (!e.latLng || suppressRef.current) return;
          const nearest = findNearest(e.latLng);
          openInfo(
            popupHtml(
              formatDateTime(nearest.ts, tz),
              eventName(nearest),
              showVehicle ? vehicleName : null,
              "hover",
            ),
            { lat: e.latLng.lat(), lng: e.latLng.lng() },
          );
        }),
        hitLine.addListener("mouseout", () => scheduleCloseInfo()),
        hitLine.addListener("click", (e: google.maps.MapMouseEvent) => {
          if (!e.latLng) return;
          closeInfo();
          onSelect(buildSelected(findNearest(e.latLng), deviceId, vehicleName, tz));
        }),
      );
    }

    const endpoints =
      events.length > 1
        ? [
            { event: events[0], color: START_COLOR, title: "Primer evento" },
            { event: events[events.length - 1], color: END_COLOR, title: "Último evento" },
          ]
        : [{ event: events[0], color: lineColor, title: eventName(events[0]) }];

    endpoints.forEach(({ event, color: markerColor, title }) => {
      const marker = new google.maps.Marker({
        position: { lat: event.lat, lng: event.lng },
        map,
        zIndex: 5,
        title,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: markerColor,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
      });

      listeners.push(
        marker.addListener("click", () => {
          closeInfo();
          onSelect(buildSelected(event, deviceId, vehicleName, tz));
        }),
      );

      markers.push(marker);
    });

    return () => {
      window.clearTimeout(closeTimer);
      listeners.forEach((l) => google.maps.event.removeListener(l));
      lines.forEach((l) => l.setMap(null));
      markers.forEach((m) => m.setMap(null));
      info.close();
    };
  }, [map, events, color, colorByUnit, tz, deviceId, vehicleName, showVehicle, onSelect]);

  return null;
};

interface ReportEventsMapProps {
  groups: MapEventGroup[];
  height?: number;
  tz?: string;
  selected?: SelectedEventPoint | null;
  onSelect?: (event: SelectedEventPoint) => void;
  onClearSelected?: () => void;
}

const noop = () => {};

const MAP_OPTIONS: google.maps.MapOptions = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  zoomControl: true,
  rotateControl: false,
  scaleControl: false,
  keyboardShortcuts: false,
  clickableIcons: false,
  draggable: true,
  gestureHandling: "greedy",
  scrollwheel: true,
};

const ReportEventsMap = memo(({
  groups,
  height = 420,
  tz,
  selected,
  onSelect = noop,
  onClearSelected = noop,
}: ReportEventsMapProps) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const resolvedTz = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const multiUnit = groups.length > 1;
  const selectedMarkerRef = useRef<google.maps.Marker | null>(null);
  const selectedInfoRef = useRef<google.maps.InfoWindow | null>(null);

  useEffect(() => {
    if (!map) return;
    const allEvents = groups.flatMap((g) => g.events);
    if (allEvents.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    allEvents.forEach((e) => bounds.extend({ lat: e.lat, lng: e.lng }));
    map.fitBounds(bounds, 60);
  }, [map, groups]);

  useEffect(() => {
    if (!map) return;

    selectedMarkerRef.current?.setMap(null);
    selectedMarkerRef.current = null;
    selectedInfoRef.current?.close();
    selectedInfoRef.current = null;

    if (!selected) return;

    const pos = { lat: selected.lat, lng: selected.lng };
    map.panTo(pos);
    if ((map.getZoom() ?? DEFAULT_ZOOM) < 14) map.setZoom(15);

    const marker = new google.maps.Marker({
      position: pos,
      map,
      zIndex: 10,
      animation: google.maps.Animation.BOUNCE,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: SELECTED_COLOR,
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2.5,
      },
    });
    selectedMarkerRef.current = marker;

    const bounceTimer = window.setTimeout(() => marker.setAnimation(null), 2100);

    const info = new google.maps.InfoWindow({
      content: popupHtml(
        selected.fecha,
        selected.evento,
        multiUnit ? selected.vehicleName : null,
        "pinned",
      ),
      position: pos,
    });
    info.open(map);
    selectedInfoRef.current = info;

    const closeListener = info.addListener("closeclick", () => onClearSelected());

    return () => {
      window.clearTimeout(bounceTimer);
      google.maps.event.removeListener(closeListener);
      marker.setMap(null);
      info.close();
    };
  }, [map, selected, multiUnit, onClearSelected]);

  if (!isLoaded) {
    return (
      <div
        className="w-full flex items-center justify-center text-sm text-gray-500"
        style={{ height }}
      >
        Cargando mapa…
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: `${height}px`, borderRadius: "0.5rem" }}
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      onLoad={(m) => setMap(m)}
      onUnmount={() => setMap(null)}
      onClick={() => onClearSelected()}
      options={MAP_OPTIONS}
    >
      {map &&
        groups.map((g) =>
          g.events.length > 0 ? (
            <EventRoute
              key={g.deviceId}
              map={map}
              events={g.events}
              color={g.color}
              colorByUnit={multiUnit}
              tz={resolvedTz}
              deviceId={g.deviceId}
              vehicleName={g.vehicleName}
              showVehicle={multiUnit}
              hasSelection={selected != null}
              onSelect={onSelect}
            />
          ) : null,
        )}
    </GoogleMap>
  );
});

export default ReportEventsMap;
